import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

const RowCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  disabled?: boolean;
  id: string;
}> = ({ checked, indeterminate, onChange, disabled, id }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <Form.Check
      ref={ref}
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="me-2"
    />
  );
};

const RESOURCE_CATEGORY_MAP: Record<string, string> = {
  dashboard: 'general',
  analytics: 'general',
  employees: 'general',
  departments: 'general',
  calendars: 'general',
  shifts: 'general',
  cameras: 'general',
  edge_devices: 'general',
  images: 'general',
  employeesVacation: 'attendanceLeave',
  holidays: 'attendanceLeave',
  validation: 'attendanceLeave',
  policy: 'attendanceLeave',
  snapshots: 'general',
  employeesAggregate: 'reports',
  facesImages: 'reports',
  attendanceRecord: 'reports',
  breakByEmployee: 'reports',
  breakByDate: 'reports',
  customReports: 'reports',
  ppe: 'ppe',
  ppe_settings: 'ppe',
  ppe_violations: 'ppe',
  payroll: 'general',
  companies: 'administration',
  roles: 'administration',
  users: 'administration',
};

const CATEGORY_ORDER = ['general', 'attendanceLeave', 'reports', 'ppe', 'administration', 'other'];
import { 
  Table, 
  Card, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal,
  Row,
  Col,
  ListGroup
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { RoleIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { authAPI } from '../utils/api';
import type { Role, ResourcePermission } from '../types/auth';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';

const RoleManagement: React.FC = () => {
  const { currentCompany } = useAuth();
  const { 
    canManageRoles, 
    canAddRoles, 
    canEditRoles, 
    canDeleteRoles,
    canViewRolesList
  } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'roleManagement' });
  const { t: tc } = useTranslation('common');
  
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  const [roles, setRoles] = useState<Role[]>([]);
  const [schema, setSchema] = useState<Record<string, string[]>>({});
  const [scopeable, setScopeable] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, { actions: string[]; scope: 'all' | 'assigned' }>>({});

  const getResourceDisplayName = useCallback((resource: string): string => {
    const key = `resourceNames.${resource}`;
    const fallback = resource
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, letter => letter.toUpperCase());
    return t(key, { defaultValue: fallback });
  }, [t]);

  const groupedSchema = useMemo(() => {
    const groups: Record<string, [string, string[]][]> = {};
    
    Object.entries(schema).forEach(([resource, actions]) => {
      const category = RESOURCE_CATEGORY_MAP[resource] || 'other';
      if (!groups[category]) groups[category] = [];
      groups[category].push([resource, actions]);
    });

    return CATEGORY_ORDER
      .filter(cat => groups[cat] && groups[cat].length > 0)
      .map(cat => ({
        category: cat,
        resources: groups[cat]
      }));
  }, [schema]);

  const toggleResourceAll = useCallback((resource: string, actions: string[]) => {
    setSelectedPermissions(prev => {
      const current = prev[resource];
      const allSelected = current?.actions.length === actions.length;
      const next = { ...prev };
      if (allSelected) {
        delete next[resource];
      } else {
        next[resource] = { actions: [...actions], scope: current?.scope || 'all' };
      }
      return next;
    });
  }, []);

  const toggleCategoryAll = useCallback((resources: [string, string[]][]) => {
    setSelectedPermissions(prev => {
      const allSelected = resources.every(([resource, actions]) =>
        prev[resource]?.actions.length === actions.length
      );
      const next = { ...prev };
      if (allSelected) {
        resources.forEach(([resource]) => { delete next[resource]; });
      } else {
        resources.forEach(([resource, actions]) => {
          next[resource] = { actions: [...actions], scope: prev[resource]?.scope || 'all' };
        });
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const next: Record<string, { actions: string[]; scope: 'all' | 'assigned' }> = {};
    Object.entries(schema).forEach(([resource, actions]) => {
      next[resource] = { 
        actions: [...actions], 
        scope: selectedPermissions[resource]?.scope || 'all' 
      };
    });
    setSelectedPermissions(next);
  }, [schema, selectedPermissions]);

  const deselectAll = useCallback(() => {
    setSelectedPermissions({});
  }, []);

  const fetchRoles = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getRoles(currentCompany.company_id);
      if (response.success) {
        setRoles(response.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  const fetchSchema = useCallback(async () => {
    try {
      setSchemaLoading(true);
      const response = await authAPI.getRolesSchema();
      if (response.success) {
        setSchema(response.data.schema || {});
        setScopeable(response.data.scopeable || []);
      }
    } catch (error: any) {
      console.error('Error fetching schema:', error);
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewRolesList) {
      fetchRoles();
    }
    fetchSchema();
  }, [fetchRoles, fetchSchema, canViewRolesList]);

  const filteredRoles = useMemo(() => {
    if (!searchTerm.trim()) return roles;
    const searchLower = searchTerm.toLowerCase();
    return roles.filter(role => 
      role.role_name?.toLowerCase().includes(searchLower)
    );
  }, [roles, searchTerm]);

  const totalPages = Math.ceil(filteredRoles.length / rowsPerPage);
  const paginatedRoles = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredRoles.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRoles, page, rowsPerPage]);

  const handleShowModal = useCallback(() => {
    setIsEditMode(false);
    setEditingRoleId(null);
    setRoleName('');
    setSelectedPermissions({});
    setModalError(null);
    setModalSuccess(null);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setIsEditMode(false);
    setEditingRoleId(null);
    setSelectedPermissions({});
  }, []);

  const handleEditRole = useCallback(async (role: Role) => {
    setIsEditMode(true);
    setEditingRoleId(role.id);
    setRoleName(role.role_name);
    
    try {
      setModalLoading(true);
      setShowModal(true);
      setModalError(null);
      setModalSuccess(null);
      
      const response = await authAPI.getRole(currentCompany!.company_id, role.id);
      if (response.success) {
        const normalized: Record<string, { actions: string[]; scope: 'all' | 'assigned' }> = {};
        Object.entries(response.data.role || {}).forEach(([resource, value]) => {
          if (Array.isArray(value)) {
            normalized[resource] = { actions: value, scope: 'all' };
          } else {
            normalized[resource] = value as { actions: string[]; scope: 'all' | 'assigned' };
          }
        });
        setSelectedPermissions(normalized);
      }
    } catch (error: any) {
      setModalError(error.response?.data?.message || t('loadRoleDetailsFailed'));
    } finally {
      setModalLoading(false);
    }
  }, [currentCompany, t]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeletingRole(null);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleDeleteRole = useCallback((role: Role) => {
    setDeletingRole(role);
    setShowDeleteModal(true);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const confirmDeleteRole = useCallback(async () => {
    if (!deletingRole || !currentCompany) return;
    
    try {
      setModalLoading(true);
      const response = await authAPI.deleteRole(currentCompany.company_id, deletingRole.id);
      if (response.success) {
        setModalSuccess(response.message || t('deleteSuccess'));
        fetchRoles();
        setTimeout(() => handleCloseDeleteModal(), 1500);
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        setModalError(t('roleInUse'));
      } else {
        setModalError(error.response?.data?.message || t('deleteFailed'));
      }
    } finally {
      setModalLoading(false);
    }
  }, [deletingRole, currentCompany, fetchRoles, t, handleCloseDeleteModal]);

  const togglePermission = (resource: string, action: string) => {
    setSelectedPermissions(prev => {
      const current = prev[resource] || { actions: [], scope: 'all' };
      let newActions;
      if (current.actions.includes(action)) {
        newActions = current.actions.filter(a => a !== action);
      } else {
        newActions = [...current.actions, action];
      }
      
      const next = { ...prev };
      if (newActions.length === 0) {
        delete next[resource];
      } else {
        next[resource] = { ...current, actions: newActions };
      }
      return next;
    });
  };

  const handleScopeChange = (resource: string, scope: 'all' | 'assigned') => {
    setSelectedPermissions(prev => {
      if (!prev[resource]) return prev;
      return {
        ...prev,
        [resource]: { ...prev[resource], scope }
      };
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;
    if (!roleName.trim()) {
      setModalError(t('roleNameRequired'));
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);

      const filteredPermissions: Record<string, ResourcePermission> = {};
      Object.entries(schema).forEach(([resource, actions]) => {
        const selected = selectedPermissions[resource];
        if (!selected) return;

        const validActions = selected.actions.filter(a => actions.includes(a));
        if (validActions.length > 0) {
          if (scopeable.includes(resource)) {
            filteredPermissions[resource] = { actions: validActions, scope: selected.scope };
          } else {
            filteredPermissions[resource] = validActions;
          }
        }
      });
      
      const payload = {
        company_id: currentCompany.company_id,
        role_name: roleName,
        role: filteredPermissions
      };

      if (isEditMode && editingRoleId) {
        const response = await authAPI.updateRole(currentCompany.company_id, editingRoleId, {
          role_name: roleName,
          role: filteredPermissions
        });
        setModalSuccess(response.message || t('updateSuccess'));
      } else {
        const response = await authAPI.createRole(payload);
        setModalSuccess(response.message || t('createSuccess'));
      }

      fetchRoles();
      setTimeout(() => handleCloseModal(), 2000);
    } catch (error: any) {
      setModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  if (!canManageRoles) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  const settingsPath = currentCompany ? `/company/${currentCompany.company_id}/settings` : '/settings';

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
          action={canAddRoles && currentCompany ? (
            <Button variant="primary" onClick={handleShowModal}>
              <i className="bi bi-plus-lg"></i> {t('addRole')}
            </Button>
          ) : undefined}
        />

        {!currentCompany && (
          <Alert variant="warning" role="alert">{t('selectCompanyWarning')}</Alert>
        )}

        {currentCompany && (
          <>
            <Card className="mb-4 shadow-sm an-panel an-settings-form-card">
              <Card.Body>
                <InputGroup>
                  <InputGroup.Text id="role-search-label"><i className="bi bi-search"></i></InputGroup.Text>
                  <Form.Control
                    placeholder={t('searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    aria-label={t('searchPlaceholder')}
                    aria-describedby="role-search-label"
                  />
                  {searchTerm && (
                    <Button variant="outline-secondary" onClick={() => setSearchTerm('')} aria-label={tc('buttons.clear')}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  )}
                </InputGroup>
              </Card.Body>
            </Card>

            {error && <Alert variant="danger" role="alert">{error}</Alert>}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">{t('loading')}</p>
              </div>
            ) : (
              <Card className="shadow-sm border-0 an-settings-table-card">
                <div className="d-md-none p-3">
                  {paginatedRoles.length > 0 ? (
                    paginatedRoles.map((role) => (
                      <Card key={role.id} className="an-data-card border-0 rounded-4 mb-3">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                            <div className="d-flex align-items-center gap-2 min-w-0">
                              <RoleIcon />
                              <div className="fw-medium">{role.role_name}</div>
                            </div>
                          </div>

                          <div className="small text-muted mb-2">
                            <div>{t('createdAt')}: {formatDateTimeInCompanyTimezone(role.created_at, companyTimezone)}</div>
                            <div>{t('updatedAt')}: {formatDateTimeInCompanyTimezone(role.updated_at, companyTimezone)}</div>
                          </div>

                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            {canEditRoles && (
                              <Button variant="link" className="p-1 text-primary text-decoration-none" onClick={() => handleEditRole(role)} title={tc('buttons.edit')} aria-label={`${tc('buttons.edit')} ${role.role_name}`}>
                                <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                              </Button>
                            )}
                            {canDeleteRoles && (
                              <Button variant="link" className="p-1 text-danger text-decoration-none" onClick={() => handleDeleteRole(role)} title={tc('buttons.delete')} aria-label={`${tc('buttons.delete')} ${role.role_name}`}>
                                <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-5 text-muted">{searchTerm ? t('noMatchSearch') : t('noRolesForCompany')}</div>
                  )}
                </div>

                <div className="d-none d-md-block">
                  <div className="table-responsive an-data-table an-panel an-settings-table">
                    <Table hover className="mb-0 an-settings-table">
                      <thead className="bg-light">
                        <tr>
                          <th className="px-4">{t('roleName')}</th>
                          <th>{t('createdAt')}</th>
                          <th>{t('updatedAt')}</th>
                          <th className="text-end px-4">{tc('labels.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRoles.length > 0 ? (
                          paginatedRoles.map((role) => (
                            <tr key={role.id}>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <RoleIcon />
                                  <span className="fw-medium">{role.role_name}</span>
                                </div>
                              </td>
                              <td><small className="text-muted">{formatDateTimeInCompanyTimezone(role.created_at, companyTimezone)}</small></td>
                              <td><small className="text-muted">{formatDateTimeInCompanyTimezone(role.updated_at, companyTimezone)}</small></td>
                              <td className="text-end px-4">
                                {canEditRoles && (
                                  <Button variant="link" className="p-1 me-3 text-primary text-decoration-none" onClick={() => handleEditRole(role)} title={tc('buttons.edit')} aria-label={`${tc('buttons.edit')} ${role.role_name}`}>
                                    <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                                  </Button>
                                )}
                                {canDeleteRoles && (
                                  <Button variant="link" className="p-1 text-danger text-decoration-none" onClick={() => handleDeleteRole(role)} title={tc('buttons.delete')} aria-label={`${tc('buttons.delete')} ${role.role_name}`}>
                                    <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center py-5 text-muted">
                              {searchTerm ? t('noMatchSearch') : t('noRolesForCompany')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                {totalPages > 1 && (
                  <Card.Footer className="px-4 py-3 an-data-card">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 overflow-auto">
                      <small className="text-muted text-nowrap">
                        {tc('messages.showing', { from: (page-1)*rowsPerPage + 1, to: Math.min(page*rowsPerPage, filteredRoles.length), total: filteredRoles.length, entity: '' })}
                      </small>
                      <Pagination className="mb-0 flex-wrap justify-content-center an-pagination" aria-label={t('paginationLabel')}>
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item key={i+1} active={i+1 === page} onClick={() => setPage(i+1)}>
                            {i+1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            )}
          </>
        )}

        <Modal show={showModal} onHide={() => !modalLoading && handleCloseModal()} size="lg">
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleFormSubmit}>
            <Modal.Body className="px-4 an-settings-page">
              {modalError && <Alert variant="danger" role="alert">{modalError}</Alert>}
              {modalSuccess && <Alert variant="success" role="status">{modalSuccess}</Alert>}
              
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-dark">{t('roleName')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('roleNamePlaceholder')}
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                  disabled={modalLoading}
                />
              </Form.Group>

              <div className="mb-2 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <Form.Label className="fw-bold text-dark mb-0">{t('permissions')}</Form.Label>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={selectAll}
                      disabled={modalLoading || (Object.keys(schema).length > 0 && Object.keys(schema).every(r => selectedPermissions[r]?.actions.length === schema[r].length))}
                    >
                      {t('selectAll')}
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={deselectAll}
                      disabled={modalLoading || Object.keys(selectedPermissions).length === 0}
                    >
                      {t('deselectAll')}
                    </Button>
                  </div>
                </div>
                {schemaLoading && <Spinner animation="border" size="sm" variant="primary" />}
              </div>
              <p className="text-muted small mb-3">{t('permissionsHint')}</p>

              <div className="permission-schema-container border rounded an-permissions-grid an-settings-permissions-grid overflow-auto">
                {groupedSchema.length > 0 ? (
                  <div className="permission-groups">
                    {groupedSchema.map(({ category, resources }) => {
                      const categoryActionsCount = resources.reduce((acc, [_, actions]) => acc + actions.length, 0);
                      const selectedCategoryActionsCount = resources.reduce((acc, [resource]) => acc + (selectedPermissions[resource]?.actions.length || 0), 0);
                      
                      const isCategoryAllSelected = selectedCategoryActionsCount === categoryActionsCount;
                      const isCategoryIndeterminate = selectedCategoryActionsCount > 0 && selectedCategoryActionsCount < categoryActionsCount;

                      return (
                        <div key={category} className="category-section border-bottom last-child-border-0">
                          <div className="bg-light px-3 py-2 border-bottom d-flex align-items-center">
                            <RowCheckbox
                              id={`cat-${category}`}
                              checked={isCategoryAllSelected}
                              indeterminate={isCategoryIndeterminate}
                              onChange={() => toggleCategoryAll(resources)}
                              disabled={modalLoading}
                            />
                            <span className="fw-bold text-uppercase small text-muted">
                              {t(`categoryNames.${category}`)}
                            </span>
                          </div>
                          <ListGroup variant="flush">
                            {resources.map(([resource, actions]) => {
                              const isScopeable = scopeable.includes(resource);
                              const selectedActions = selectedPermissions[resource]?.actions || [];
                              const isResourceAllSelected = selectedActions.length === actions.length;
                              const isResourceIndeterminate = selectedActions.length > 0 && selectedActions.length < actions.length;
                              const isResourceSelected = selectedActions.length > 0;
                              
                              return (
                                <ListGroup.Item key={resource} className="py-3 ps-4">
                                  <Row className="align-items-center">
                                    <Col md={4} className="d-flex align-items-center">
                                      <RowCheckbox
                                        id={`res-${resource}`}
                                        checked={isResourceAllSelected}
                                        indeterminate={isResourceIndeterminate}
                                        onChange={() => toggleResourceAll(resource, actions)}
                                        disabled={modalLoading}
                                      />
                                      <span className="fw-bold text-secondary">
                                        {getResourceDisplayName(resource)}
                                      </span>
                                    </Col>
                                    <Col md={isScopeable && isResourceSelected ? 5 : 8}>
                                      <div className="d-flex flex-wrap gap-3">
                                        {actions.map(action => (
                                          <Form.Check
                                            key={`${resource}-${action}`}
                                            type="checkbox"
                                            id={`perm-${resource}-${action}`}
                                            label={<span className="text-capitalize small">{action}</span>}
                                            checked={selectedPermissions[resource]?.actions.includes(action) || false}
                                            onChange={() => togglePermission(resource, action)}
                                            disabled={modalLoading}
                                          />
                                        ))}
                                      </div>
                                    </Col>
                                    {isScopeable && isResourceSelected && (
                                      <Col md={3}>
                                        <div className="d-flex align-items-center gap-2">
                                          <Form.Label className="small mb-0 text-muted">{t('scopeLabel')}:</Form.Label>
                                          <Form.Select
                                            size="sm"
                                            value={selectedPermissions[resource]?.scope || 'all'}
                                            onChange={(e) => handleScopeChange(resource, e.target.value as 'all' | 'assigned')}
                                            disabled={modalLoading}
                                            className="flex-grow-1"
                                          >
                                            <option value="all">{t('scopeAll')}</option>
                                            <option value="assigned">{t('scopeAssigned')}</option>
                                          </Form.Select>
                                        </div>
                                      </Col>
                                    )}
                                  </Row>
                                </ListGroup.Item>
                              );
                            })}
                          </ListGroup>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center text-muted">
                    {schemaLoading ? t('loadingSchema') : t('noSchemaAvailable')}
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="px-4 an-section-divider">
              <Button variant="outline-secondary" onClick={() => handleCloseModal()} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : (isEditMode ? t('updateRoleBtn') : t('createRoleBtn'))}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showDeleteModal} onHide={() => !modalLoading && handleCloseDeleteModal()}>
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{t('deleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 text-center">
            {modalError && <Alert variant="danger" role="alert">{modalError}</Alert>}
            {modalSuccess && <Alert variant="success" role="status">{modalSuccess}</Alert>}
            {!modalSuccess && (
              <>
                <div className="my-3">
                  <i className="bi bi-exclamation-triangle text-danger mb-3" style={{ fontSize: '64px' }}></i>
                  <p className="lead">{t('deleteConfirmMessage', { name: deletingRole?.role_name })}</p>
                  <p className="text-muted small">{t('deleteWarning')}</p>
                </div>
              </>
            )}
          </Modal.Body>
          {!modalSuccess && (
            <Modal.Footer className="justify-content-center">
              <Button variant="outline-secondary" onClick={() => handleCloseDeleteModal()} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="danger" onClick={confirmDeleteRole} disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : t('deleteRoleBtn')}
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      </Container>
    </Layout>
  );
};

export default RoleManagement;


